/**
 * versona_metrics — PURE aggregate metrics for the Versona Universe "Metrics" tab
 * (2026-09-18: "make sure the versona universe window is up to date with its
 * metrics and not missing any metrics about versonas").
 *
 * Every number here is computed from plain rows the window already snapshots
 * over SQL (versona, npc_agent, origin, lineage, pairs, favors, grudges,
 * households) plus the smaller live tables it now also snapshots (journeys,
 * goals, war duties, conditions, air hulls, battle units, graves, migrations,
 * offices). No DOM, no three: node-testable (`verify:versona-metrics`).
 *
 * Life stage mirrors the server's `lifeStageOf` (civilization_ops):
 * a lineage row younger than JUVENILE_REAL_HOURS is a child, older than
 * ELDER_REAL_DAYS an elder; a founder (no lineage) is an adult.
 */

export const JUVENILE_REAL_HOURS = 48;
export const ELDER_REAL_DAYS = 60;

export interface MVersona { id: string; archetype: string; state: string; homeCityId: string }
export interface MAgent { id: string; settlementId: string; job: string; hunger: number; needRest: number; needSocial: number; mood: number; wallet: number; boldness?: number }
export interface MLineage { childId: string; bornAtMs: number; generation: number }
export interface MPair { aId: string; bId: string; familiarity: number }
export interface MFavor { aId: string; bId: string; balance: number }
export interface MGrudge { versonaId: string; targetId: string; intensity: number; causeText: string }
export interface MHousehold { householdId: string; members: number }
export interface MJourney { reason: string; kind: string }
export interface MGoal { state: string }
export interface MDuty { kind: string }
export interface MCondition { injuryTier: number; fitAtMs: number; battleId: string }
export interface MAir { side: string; kind: string; state: string; hp: number }
export interface MBattleUnit { side: string; state: string; role: string; hp: number; morale: number }
export interface MGrave { cause: string; diedAtMs: number; settlementId: string }
export interface MMigration { reason: string; atMs: number }
export interface MOffice { office: string; settlementId: string }
/** SOCIETY WAVE (2026-09-19): the wave's own rows — all optional, so an older snapshot still computes. */
export interface MTownScore { settlementId: string; cohesion: number; readiness: number; learning: number; health: number; order: number; piety: number; prosperity: number }
export interface MFederation { civ: string; purse: number; levyBps: number; customsBps: number; legitimacy: number; speaker: string; atWarWith: string; reserveGrain: number; members: number; suspended: number; defiant: number; openBills: number; passedBills: number; works: number; worksDone: number; casesOpen: number; casesAccepted: number; casesRefused: number }
export interface MSociety {
  /** versonaId → the title they are known by (a post, a practised craft, else the base job). */
  titles: Map<string, string>;
  postsOpen: number; postsHeld: number;
  postsByKey: Array<{ key: string; held: number; open: number }>;
  scores: MTownScore[];
  federations: MFederation[];
  madeItems: Array<{ itemKey: string }>;
  stations: number;
  tradeOrders: number;
  marketLinks: number;
}

export interface MetricsInput {
  nowMs: number;
  versonas: MVersona[];
  agents: MAgent[];
  civOfSettlement: Map<string, string>;   // settlementId → 'a' | 'b'
  originCiv: Map<string, string>;         // versonaId → born civ
  tierOfSettlement: Map<string, string>;  // settlementId → city | town | hamlet | capital | …
  lineage: MLineage[];
  pairs: MPair[];
  favors: MFavor[];
  grudges: MGrudge[];
  households: MHousehold[];
  journeys: MJourney[];
  goals: MGoal[];
  duties: MDuty[];
  conditions: MCondition[];
  air: MAir[];
  battleUnits: MBattleUnit[];
  graves: MGrave[];
  migrations: MMigration[];
  offices: MOffice[];
  society?: MSociety;
}

export interface Stat { n: number; mean: number; median: number; min: number; max: number; sum: number }
/**
 * A row on a card. `drill` makes it a DOOR: the ids or names behind the number,
 * so "33 empty guild halls" can be opened instead of read (2026-09-20 — the
 * cards were dead ends). Populated only where the rows are cheap to carry;
 * capped at DRILL_CAP so a metric over ten thousand versonas stays a number.
 */
export interface Metric { label: string; value: string; hint?: string; drill?: Drill }
/** `versona` ids open in the roster; `text` is just a named list (guilds, commodities). */
export interface Drill { kind: 'versona' | 'text'; label: string; ids: string[]; more?: number }
export const DRILL_CAP = 200;
/** Build a capped drill-down, or nothing when there is nothing behind the number. */
export function drill(kind: 'versona' | 'text', label: string, ids: readonly string[]): Drill | undefined {
  if (ids.length === 0) return undefined;
  return { kind, label, ids: ids.slice(0, DRILL_CAP).map(String), more: Math.max(0, ids.length - DRILL_CAP) };
}
/**
 * Which filter chip a card answers to (Metrics tab, 2026-09-19). `people` is the
 * original tab; the rest were added when the tab grew past one screen and a flat
 * grid of twenty-six cards stopped being readable.
 */
export type MetricCat = 'people' | 'mind' | 'gov' | 'law' | 'space' | 'guilds' | 'war' | 'econ' | 'pvp' | 'craft' | 'sim';
export const METRIC_CATS: ReadonlyArray<{ id: MetricCat; label: string }> = [
  { id: 'people', label: 'People' }, { id: 'mind', label: 'Mind' }, { id: 'gov', label: 'Government' },
  { id: 'law', label: 'Law' }, { id: 'space', label: 'Space' }, { id: 'guilds', label: 'Guilds' },
  { id: 'war', label: 'War' }, { id: 'econ', label: 'Economy' }, { id: 'pvp', label: 'PvP' },
  { id: 'craft', label: 'Crafting' }, { id: 'sim', label: 'Sim health' },
];
export interface MetricGroup {
  id: string; title: string; cat: MetricCat; metrics: Metric[];
  bars?: Array<{ label: string; value: number; share: number }>;
  /** A two-column card (civ A against civ B). Rendered instead of bars. */
  columns?: [string, string];
  pairs?: Array<{ label: string; a: string; b: string; hint?: string }>;
}

export function stat(values: number[]): Stat {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  const n = v.length;
  if (n === 0) return { n: 0, mean: 0, median: 0, min: 0, max: 0, sum: 0 };
  const sum = v.reduce((t, x) => t + x, 0);
  return { n, mean: sum / n, median: n % 2 ? v[(n - 1) / 2] : (v[n / 2 - 1] + v[n / 2]) / 2, min: v[0], max: v[n - 1], sum };
}

/** Gini coefficient of a non-negative distribution (0 = equal, 1 = one holds all). */
export function gini(values: number[]): number {
  const v = values.filter((x) => Number.isFinite(x) && x >= 0).sort((a, b) => a - b);
  const n = v.length;
  if (n < 2) return 0;
  const sum = v.reduce((t, x) => t + x, 0);
  if (sum <= 0) return 0;
  let acc = 0;
  for (let i = 0; i < n; i++) acc += (2 * (i + 1) - n - 1) * v[i];
  return acc / (n * sum);
}

/** Counts by key, sorted by count desc, with shares. */
export function tally(keys: string[]): Array<{ label: string; value: number; share: number }> {
  const m = new Map<string, number>();
  for (const k of keys) m.set(k, (m.get(k) ?? 0) + 1);
  const total = keys.length || 1;
  return [...m.entries()].map(([label, value]) => ({ label, value, share: value / total })).sort((a, b) => b.value - a.value || (a.label < b.label ? -1 : 1));
}

export type LifeStage = 'child' | 'adult' | 'elder';
export function lifeStageAt(nowMs: number, bornAtMs: number | null): LifeStage {
  if (bornAtMs === null) return 'adult';
  const hours = (nowMs - bornAtMs) / 3_600_000;
  if (hours < JUVENILE_REAL_HOURS) return 'child';
  return hours > ELDER_REAL_DAYS * 24 ? 'elder' : 'adult';
}

export const pct = (x: number): string => `${(x * 100).toFixed(x >= 0.1 ? 0 : 1)} %`;
export const f1 = (x: number): string => (Math.abs(x) >= 100 ? x.toFixed(0) : x.toFixed(1));
export const int = (x: number): string => Math.round(x).toLocaleString('en-US');

/** The whole board: groups of metrics, each a label + a formatted value (+ bars where a distribution reads better). */
export function computeMetrics(inp: MetricsInput): MetricGroup[] {
  const out: MetricGroup[] = [];
  const agentOf = new Map(inp.agents.map((a) => [a.id, a]));
  const bornOf = new Map(inp.lineage.map((l) => [l.childId, l.bornAtMs]));
  const vs = inp.versonas;
  const n = vs.length || 1;

  // ── Population ──
  const civTally = tally(vs.map((v) => inp.originCiv.get(v.id) ?? inp.civOfSettlement.get(agentOf.get(v.id)?.settlementId ?? '') ?? '?'));
  const stages = tally(vs.map((v) => lifeStageAt(inp.nowMs, bornOf.get(v.id) ?? null)));
  const tiers = tally(vs.map((v) => inp.tierOfSettlement.get(agentOf.get(v.id)?.settlementId ?? '') ?? 'unhoused'));
  const sites = new Set(inp.agents.map((a) => a.settlementId));
  const gens = inp.lineage.map((l) => l.generation);
  out.push({
    id: 'population', cat: 'people', title: 'Population',
    metrics: [
      { label: 'versonas', value: int(vs.length) },
      { label: 'with an agent row', value: `${int(inp.agents.length)} (${pct(inp.agents.length / n)})`, hint: 'npc_agent — the economy/needs row every settled versona carries' },
      { label: 'settlements peopled', value: int(sites.size) },
      { label: 'born here (lineage rows)', value: int(inp.lineage.length), hint: 'founders have no lineage row' },
      { label: 'deepest generation', value: gens.length ? int(Math.max(...gens)) : '0' },
      { label: 'households', value: int(inp.households.length) },
      { label: 'mean household', value: f1(stat(inp.households.map((h) => h.members)).mean) },
    ],
    bars: [...civTally.map((b) => ({ ...b, label: `civ ${b.label}` })), ...stages, ...tiers],
  });

  // ── Jobs & states ──
  const jobs = tally(inp.agents.map((a) => a.job));
  const states = tally(vs.map((v) => v.state));
  out.push({ id: 'jobs', cat: 'people', title: 'Jobs (top 14)', metrics: [{ label: 'distinct jobs', value: int(jobs.length) }], bars: jobs.slice(0, 14) });
  if (inp.society) {
    // PROFESSIONS: what a versona is KNOWN as — the post they hold, the craft they practise, else the base job.
    const soc = inp.society;
    const titles = tally(inp.agents.map((a) => soc.titles.get(a.id) ?? a.job.replace(/_/g, ' ')));
    out.push({
      id: 'professions', cat: 'people', title: 'Professions (by earned title)',
      metrics: [
        { label: 'distinct professions', value: int(titles.length), hint: 'the 13 base jobs + every craft a versona practises + every service post held' },
        { label: 'service posts held / open', value: `${int(soc.postsHeld)} / ${int(soc.postsOpen)}`, hint: 'svc: job slots — paid by the treasury or by customers, once per market day' },
        { label: 'items made to order (kept)', value: int(soc.madeItems.length) },
        { label: 'scarcity orders open', value: int(soc.tradeOrders), hint: 'a town posts a premium for a good no neighbour\'s caravan brings' },
        { label: 'market-city links', value: int(soc.marketLinks), hint: 'a hamlet\'s or town\'s trading habit — never an overlord' },
      ],
      bars: titles.slice(0, 20),
    });
    out.push({
      id: 'posts', cat: 'people', title: 'Service posts',
      metrics: [{ label: 'kinds staffed', value: int(soc.postsByKey.filter((p) => p.held > 0).length) }, { label: 'activity stations seeded', value: int(soc.stations) }],
      bars: soc.postsByKey.filter((p) => p.held + p.open > 0).sort((x, y) => y.held - x.held).slice(0, 16)
        .map((p) => ({ label: `${p.key.replace('svc:', '').replace(/_/g, ' ')} (${p.open} open)`, value: p.held, share: p.held / Math.max(1, p.held + p.open) })),
    });
  }
  out.push({ id: 'states', cat: 'people', title: 'Activity states', metrics: [{ label: 'distinct states', value: int(states.length) }], bars: states.slice(0, 14) });

  // ── Needs & mood ──
  const hunger = stat(inp.agents.map((a) => a.hunger)), rest = stat(inp.agents.map((a) => a.needRest)), social = stat(inp.agents.map((a) => a.needSocial));
  const moodA = stat(inp.agents.map((a) => a.mood));
  out.push({
    id: 'needs', cat: 'people', title: 'Needs & mood',
    metrics: [
      { label: 'hunger mean / median', value: `${f1(hunger.mean)} / ${f1(hunger.median)}`, hint: 'npc_agent.hunger (0 fed … 100 starving)' },
      { label: 'hungry (> 70)', value: pct(inp.agents.filter((a) => a.hunger > 70).length / Math.max(1, inp.agents.length)) },
      { label: 'rest need mean', value: f1(rest.mean) },
      { label: 'social need mean', value: f1(social.mean) },
      { label: 'mood mean / median', value: `${f1(moodA.mean)} / ${f1(moodA.median)}`, hint: 'npc_agent.mood' },
      { label: 'low mood (< 30)', value: pct(inp.agents.filter((a) => a.mood < 30).length / Math.max(1, inp.agents.length)) },
    ],
    bars: histogram(inp.agents.map((a) => a.mood), [0, 20, 40, 60, 80, 101], 'mood'),
  });

  // ── Wealth ──
  const wallets = inp.agents.map((a) => a.wallet);
  const w = stat(wallets);
  const sorted = [...wallets].sort((a, b) => b - a);
  const top10 = sorted.slice(0, Math.max(1, Math.ceil(sorted.length * 0.1))).reduce((t, x) => t + x, 0);
  out.push({
    id: 'wealth', cat: 'econ', title: 'Wealth (coins in versona wallets)',
    metrics: [
      { label: 'total coins', value: int(w.sum) },
      { label: 'mean / median', value: `${f1(w.mean)} / ${f1(w.median)}` },
      { label: 'richest', value: int(w.max) },
      { label: 'top 10 % hold', value: pct(w.sum > 0 ? top10 / w.sum : 0) },
      { label: 'Gini', value: gini(wallets).toFixed(3), hint: '0 equal … 1 one holds all' },
      { label: 'broke (< 20)', value: pct(wallets.filter((x) => x < 20).length / Math.max(1, wallets.length)) },
    ],
    bars: histogram(wallets, [0, 50, 100, 250, 500, 1000, Infinity], 'ⓒ'),
  });

  // ── Bonds ──
  const fam = stat(inp.pairs.map((p) => p.familiarity));
  const fav = stat(inp.favors.map((f) => Math.abs(f.balance)));
  const gr = stat(inp.grudges.map((g) => g.intensity));
  const causes = tally(inp.grudges.map((g) => g.causeText.split(/[.,;:]/)[0].trim().slice(0, 40) || '?'));
  out.push({
    id: 'bonds', cat: 'people', title: 'Bonds, favors, grudges',
    metrics: [
      { label: 'acquainted pairs', value: int(inp.pairs.length), hint: 'versona_pair' },
      { label: 'familiarity mean / max', value: `${f1(fam.mean)} / ${f1(fam.max)}` },
      { label: 'pairs per versona', value: f1((inp.pairs.length * 2) / n) },
      { label: 'favor ledgers', value: int(inp.favors.length) },
      { label: 'mean |favor balance|', value: f1(fav.mean) },
      { label: 'grudges', value: int(inp.grudges.length) },
      { label: 'grudge intensity mean / max', value: `${f1(gr.mean)} / ${f1(gr.max)}` },
      { label: 'versonas holding a grudge', value: int(new Set(inp.grudges.map((g) => g.versonaId)).size) },
    ],
    bars: causes.slice(0, 8),
  });

  // ── Movement ──
  out.push({
    id: 'movement', cat: 'people', title: 'Movement',
    metrics: [
      { label: 'journeys underway', value: int(inp.journeys.length), hint: 'versona_journey (by ship or hovercraft)' },
      { label: 'by air / by land', value: `${int(inp.journeys.filter((j) => j.kind === 'air').length)} / ${int(inp.journeys.filter((j) => j.kind === 'land').length)}` },
      { label: 'goals set', value: int(inp.goals.length), hint: 'versona_goal — the brain\'s or a scene\'s order' },
    ],
    bars: [...tally(inp.journeys.map((j) => `journey · ${j.reason}`)).slice(0, 6), ...tally(inp.goals.map((g) => `goal · ${g.state}`)).slice(0, 8)],
  });

  // ── War ──
  const injured = inp.conditions.filter((c) => c.injuryTier > 0 && c.fitAtMs > inp.nowMs);
  out.push({
    id: 'war', cat: 'war', title: 'War duties, injuries, hulls',
    metrics: [
      { label: 'on a war duty', value: int(inp.duties.length), hint: 'war_assignment (one duty per versona)' },
      { label: 'injured (unfit)', value: `${int(injured.length)} · severe ${int(injured.filter((c) => c.injuryTier >= 2).length)}`, hint: 'unit_condition.fitForDutyAt in the future' },
      { label: 'hulls in the sky', value: int(inp.air.filter((a) => a.state !== 'down' && a.state !== 'home' && a.state !== 'depart').length) },
      { label: 'hulls down / departing', value: `${int(inp.air.filter((a) => a.state === 'down').length)} / ${int(inp.air.filter((a) => a.state === 'depart' || a.state === 'home').length)}` },
      { label: 'battle units on fields', value: int(inp.battleUnits.length) },
      { label: 'fallen this battle set', value: int(inp.battleUnits.filter((u) => u.state === 'fallen').length) },
    ],
    bars: [...tally(inp.duties.map((d) => `duty · ${d.kind}`)).slice(0, 6), ...tally(inp.battleUnits.map((u) => `unit · ${u.state}`)).slice(0, 10)],
  });

  // ── Graves & migrations ──
  const day = 86_400_000;
  out.push({
    id: 'mortality', cat: 'people', title: 'Graves & migrations',
    metrics: [
      { label: 'graves', value: int(inp.graves.length) },
      { label: 'deaths last 24 h / 7 d', value: `${int(inp.graves.filter((g) => inp.nowMs - g.diedAtMs < day).length)} / ${int(inp.graves.filter((g) => inp.nowMs - g.diedAtMs < 7 * day).length)}` },
      { label: 'migrations logged', value: int(inp.migrations.length) },
      { label: 'migrations last 24 h', value: int(inp.migrations.filter((m) => inp.nowMs - m.atMs < day).length) },
    ],
    bars: [...tally(inp.graves.map((g) => `death · ${g.cause}`)), ...tally(inp.migrations.map((m) => `move · ${m.reason}`)).slice(0, 6)],
  });

  // ── Offices ──
  out.push({
    id: 'offices', cat: 'gov', title: 'Offices held',
    metrics: [{ label: 'offices', value: int(inp.offices.length) }, { label: 'settlements with an office', value: int(new Set(inp.offices.map((o) => o.settlementId)).size) }],
    bars: tally(inp.offices.map((o) => o.office.startsWith('alderman:') ? 'alderman' : o.office)).slice(0, 16),
  });

  if (inp.society) {
    const soc = inp.society;
    // ── Town scores (the seven rolling scores an activity feeds; every effect is capped) ──
    const keys = ['cohesion', 'readiness', 'learning', 'health', 'order', 'piety', 'prosperity'] as const;
    out.push({
      id: 'scores', cat: 'gov', title: 'Town scores (0–100)',
      metrics: [
        { label: 'settlements scored', value: int(soc.scores.length) },
        ...keys.map((k) => ({ label: `${k} · mean / max`, value: `${f1(stat(soc.scores.map((s) => s[k])).mean)} / ${f1(stat(soc.scores.map((s) => s[k])).max)}` })),
      ],
      bars: keys.map((k) => { const m = stat(soc.scores.map((s) => s[k])).mean; return { label: k, value: Math.round(m), share: m / 100 }; }),
    });
    // ── The federations ──
    for (const f of soc.federations) {
      out.push({
        id: `federation-${f.civ}`, cat: 'gov', title: `Federation · civ ${f.civ}`,
        metrics: [
          { label: 'Speaker', value: f.speaker || '(vacant)' },
          { label: 'purse (coins)', value: int(f.purse), hint: 'the federal ledger book at the seat — levy + customs in, salaries / relief / works out' },
          { label: 'levy / customs', value: `${f1(f.levyBps / 100)} % / ${f1(f.customsBps / 100)} %`, hint: 'the levy never exceeds 10 % of a member\'s INFLOW' },
          { label: 'legitimacy', value: pct(f.legitimacy), hint: 'under 25 % only relief can pass' },
          { label: 'members · suspended · defiant', value: `${int(f.members)} · ${int(f.suspended)} · ${int(f.defiant)}`, hint: 'no levy, no vote; a town that refused the Tribunal and went to war anyway forfeits its votes while it lasts' },
          { label: 'bills open / passed', value: `${int(f.openBills)} / ${int(f.passedBills)}` },
          { label: 'works building / done', value: `${int(f.works)} / ${int(f.worksDone)}` },
          { label: 'Tribunal open / accepted / refused', value: `${int(f.casesOpen)} / ${int(f.casesAccepted)} / ${int(f.casesRefused)}` },
          { label: 'reserve grain', value: f1(f.reserveGrain) },
          { label: 'at war with', value: f.atWarWith ? `civ ${f.atWarWith}` : 'no one' },
        ],
      });
    }
  }
  return out;
}

/** Bucket counts for a histogram: edges [e0, e1, …] give bins [e0,e1), [e1,e2) …; the last edge may be Infinity. */
export function histogram(values: number[], edges: number[], unit: string): Array<{ label: string; value: number; share: number }> {
  const out: Array<{ label: string; value: number; share: number }> = [];
  const total = values.length || 1;
  for (let i = 0; i + 1 < edges.length; i++) {
    const lo = edges[i], hi = edges[i + 1];
    const c = values.filter((v) => v >= lo && v < hi).length;
    out.push({ label: `${unit} ${lo}–${hi === Infinity ? '∞' : hi}`, value: c, share: c / total });
  }
  return out;
}
